<script>
    import {
        formatNumber,
        percentOfRange,
        isValidNumber,
        trackEngagement,
    } from '../utils';

    export let label = '';
    export let currentValue;
    export let value;
    export let markers;
    export let markerValueKey;
    export let min;
    export let max;
    export let decimals;
    export let onUpdate = () => {};
    export let formatValue = (val) => val;

    let valueDisplayEl;
    let sliderValue;

    const inputId = label.toLowerCase().replace(/[^a-z0-9]/g, '');
    const stepSize = (1 / Math.pow(10, decimals)) / 2;

    const THUMB_SIZE = 16;

    function trackSizeAdjustedPercentOfRange(newValue, min, max) {
        const percent = percentOfRange(newValue, min, max);
        const diffFromFifty = 50 - percent;

        const adjustmentRatio = diffFromFifty / 50;

        return { percent, pixelAdjustment: adjustmentRatio * (THUMB_SIZE / 2) };
    }

    function getMarkerStyle(val) {
        const { percent, pixelAdjustment } = trackSizeAdjustedPercentOfRange(val, min, max);

        return `left: calc(${percent}% + ${pixelAdjustment}px)`;
    }

    function onRangeUpdate(newValue) {
        if (!newValue || newValue === value) {
            return;
        }

        onUpdate(Number(newValue));
    }

    function setValue(newValue) {
        if (isValidNumber(newValue)) {
            sliderValue = newValue;

            if (valueDisplayEl) {
                const { percent, pixelAdjustment } = trackSizeAdjustedPercentOfRange(newValue, min, max);
                const clampedPercent = Math.min(Math.max(percent, 4), 95);
                valueDisplayEl.style.left = `calc(${clampedPercent}% + ${pixelAdjustment}px)`;
            }
        }
    }

    $: setValue(value);
    $: setValue(currentValue);
    $: onRangeUpdate(sliderValue);
</script>

<style>
    label {
        font-size: 16px;
        font-weight: 700;
    }

    .slider-container {
        margin-top: 30px;
    }

    .range-input {
        width: 100%;
        position: relative;
        margin: 22px auto 0;
        height: 32px;
    }

    .range-input .slider-track {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        width: 100%;
        height: 2px;
        background: var(--color-dark);
    }

    .range-input input {
        position: absolute;
        width: 100%;
        z-index: 2;
    }

    .range-input span.value {
        position: absolute;
        top: 0px;
        left: 50%;
        transform: translate(-50%, -100%);
        font-feature-settings: 'tnum';
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
    }

    .range-input .marker-container {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
    }

    .range-input .marker-container .marker {
        position: relative;
        width: 10px;
        height: 10px;
        border-radius: 10px;
        pointer-events: none;
        background-color: var(--marker-color);
    }

    .range-input .marker-container .marker:after {
        content: "";
        position: absolute;
        bottom: 0;
        height: 12px;
        width: 2px;
        background: inherit;
        left: 50%;
        transform: translate(-50%, 100%);
    }

    input.slider {
        -webkit-appearance: none;
        appearance: none;
        height: 32px;
        margin: 0;
        padding: 0;
        background-color: transparent;
    }

    input.slider::-webkit-slider-thumb {
        -webkit-appearance: none; /* Override default look */
        appearance: none;
        margin-top: 0px; /* Centers thumb on the track */
        background-color: var(--color-interaction);
        border: 2px solid var(--color-light);
        border-top: 0px solid transparent;
        border-bottom: 0px solid transparent;
        border-radius: 10px;
        height: 26px;
        width: 16px;
    }

    input.slider::-moz-range-thumb {
        -webkit-appearance: none; /* Override default look */
        appearance: none;
        margin-top: 0px; /* Centers thumb on the track */
        background-color: var(--color-interaction);
        border: 2px solid var(--color-light);
        border-top: 0px solid transparent;
        border-bottom: 0px solid transparent;
        border-radius: 10px;
        height: 26px;
        width: 15px;
    }

    input.slider::-ms-thumb {
        -webkit-appearance: none; /* Override default look */
        appearance: none;
        margin-top: 0px; /* Centers thumb on the track */
        background-color: var(--color-interaction);
        border: 2px solid var(--color-light);
        border-top: 0px solid transparent;
        border-bottom: 0px solid transparent;
        border-radius: 10px;
        height: 28px;
        width: 16px;
    }

    @media (max-width: 600px) {
        label {
            font-size: 14px;
        }
    }

    @media (max-width: 550px) {
        .slider-container {
            margin-top: 24px;
        }

        .slider-container.oljeprisen {
            margin-top: 0;
        }
    }
</style>

<div class={`slider-container ${inputId}`}>
    <label for={inputId}>{label}</label>
    <div class="range-input">
        <span class="value" bind:this={valueDisplayEl}>{formatValue(formatNumber(value, decimals))}</span>
        {#each markers as marker}
            {#if isValidNumber(marker[markerValueKey])}
                <div
                    class="marker-container"
                    style={getMarkerStyle(marker[markerValueKey])}
                >
                    <div class="marker" style="--marker-color: {marker.color}" />
                </div>
            {/if}
        {/each}
        <div class="slider-track"></div>
        <input
            type="range"
            class="slider"
            id={inputId}
            min={min}
            max={max}
            step={stepSize}
            bind:value={sliderValue}
            on:change={() => trackEngagement('petroleum-slider', inputId)}
        />
    </div>
</div>
